// Shared plate/board geometry — every size is a dimensionless MULTIPLE of one
// viewport unit: the ball's own size, defined in CSS as `--sb-unit` (a `dvh`
// length on `.sb-size--base` / `.sb-size--compact`). CSS renders the static
// plates as `calc(var(--sb-unit) * N)`; JS uses the SAME multipliers for the
// flight overlay's `translate(calc(var(--sb-unit) * N), …)`, so the two can
// never drift. Nothing in this file, or anywhere in the board's sizing, is a
// pixel literal.

export const SLOT_HEIGHT = 1
export const PLATE_BODY_HEIGHT_BASE = 550 / 122
export const PLATE_BODY_TOP = 20 / 122
export const BASE_PLATE_HEIGHT = 588 / 122
export const BALL_SIZE = 1
// Plate button width — fixed chrome for the default board; the ball is centered
// inside it via ballLeft(sizing). A "high" capacity board passes a smaller
// Sizing (compact `--sb-unit`) so the whole plate (ball + body) shrinks
// proportionally.
export const PLATE_WIDTH = 220 / 122
// Fixed chrome below the lowest ball (plate base). Kept proportional with the
// board so a smaller plate keeps the same base proportion.
export const BASE_OFFSET = 28 / 122

// Every render dimension of a plate/board, grouped so a variant can swap the
// whole look (smaller ball + shorter pipe) without touching component code.
// All numbers are multiples of the size class's `--sb-unit`. DEFAULT_SIZING
// reproduces the original Ball Sort board's proportions exactly. The two base
// heights intentionally differ: the plate graphic uses plateHeightBase, while
// the board's row height uses plateBodyTop + plateBodyHeightBase (the plate
// overhangs its row by a constant — preserved so the layout is unchanged).
export interface Sizing {
  ballSize: number
  baseOffset: number
  boardHeightBase: number
  slotHeight: number
  plateBodyHeightBase: number
  plateBodyTop: number
  plateHeightBase: number
  // Plate pitch — the fixed distance between consecutive plate buttons,
  // expressed in units per size class (base: 240/122, compact: 240/102) so
  // the relative gap survives the compact unit.
  plateSpacing: number
  plateWidth: number
  // Naming the CSS size class that carries this Sizing's numbers (and its
  // `--sb-unit`): "base" (122px-ball proportions) or "compact" (102px ball).
  sizeKey: "base" | "compact"
}

export const DEFAULT_SIZING: Sizing = {
  ballSize: BALL_SIZE,
  baseOffset: BASE_OFFSET,
  boardHeightBase: PLATE_BODY_TOP + PLATE_BODY_HEIGHT_BASE,
  slotHeight: SLOT_HEIGHT,
  plateBodyHeightBase: PLATE_BODY_HEIGHT_BASE,
  plateBodyTop: PLATE_BODY_TOP,
  plateHeightBase: BASE_PLATE_HEIGHT,
  plateSpacing: 240 / 122,
  plateWidth: PLATE_WIDTH,
  sizeKey: "base",
}

// Left edge of a ball placed inside its plate (unit multiples). Plate.tsx and
// the flying-ball overlay both call this, so a ball never shifts while it is
// in flight — both multiply by the same `--sb-unit`.
export const ballLeft = (sizing: Sizing = DEFAULT_SIZING): number=>
  (sizing.plateWidth - sizing.ballSize) / 2
export const BALL_LEFT = ballLeft()

// Plate button pitch — the base class's spacing, kept as a module constant for
// the default board. Board math reads `sizing.plateSpacing` (per size class),
// so a compact board keeps its own wider relative gap.
export const PLATE_SPACING = 240 / 122
// Gap between the two plate rows — keep in sync with `.sb-board`'s
// `--sb-row-gap` (base class).
export const ROW_GAP = 24 / 122

// Flight duration of a single moving ball. Group moves keep the one-after-the-
// other beat but 30% quicker per ball (600ms - 30% = 420ms).
export const FLIGHT_SINGLE_MS = 600

// Bottom edge of the lowest ball in a `capacity`-tall plate — the resting line
// a stack sits on. Derived from the plate body's real bounds (plateBodyTop down
// to plateBodyTop + body height) so a FULL stack is vertically centred in the
// plate: the gap above the top ball equals the gap below the bottom ball. A
// partial stack simply rests on the same line. Both the static Plate and the
// flying-ball overlay call this, so a ball never drifts between the two.
export const stackBottom = (
  capacity: number,
  sizing: Sizing = DEFAULT_SIZING,
): number=> {
  const plateHeight =
    sizing.plateBodyHeightBase + (capacity - 4) * sizing.slotHeight
  const plateCenter = sizing.plateBodyTop + plateHeight / 2
  const stackHeight = (capacity - 1) * sizing.slotHeight + sizing.ballSize
  return plateCenter + stackHeight / 2
}

// Top edge of a ball sitting in `slot` (0 = bottom), relative to the plate.
// `sizing` lets a "high" capacity board shrink the balls and the pipe together
// so the stack stays proportional; default keeps current rendering identical.
export const ballSlotTop = (
  capacity: number,
  slot: number,
  sizing: Sizing = DEFAULT_SIZING,
): number=> stackBottom(capacity, sizing) - sizing.ballSize - slot * sizing.slotHeight

// How tall a row of plates is drawn: the plate body's bottom edge. Every row is
// as tall as the tallest plate on the board, so shorter plates in a mixed row
// leave a strip of empty row underneath them.
export const rowHeight = (
  capacityMax: number,
  sizing: Sizing = DEFAULT_SIZING,
): number=> sizing.boardHeightBase + (capacityMax - 4) * sizing.slotHeight

// CSS size class naming the --sb-* custom-property block (board-geometry.css)
// that carries a Sizing's numbers — and its `--sb-unit` dvh length. Two sizings
// ship: base (122px-ball proportions) and compact (102px ball, the high/spread
// siblings). The class lets CSS consume the same numbers the JS flight math
// uses.
export const sizingClass = (sizing: Sizing = DEFAULT_SIZING): string=>
  sizing.sizeKey === "compact" ? "sb-size--compact" : "sb-size--base"
